import React, {useContext, useEffect, useRef} from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";
import CountryContext from "../../CountryContext";
import NotificationsCount from "../NotificationsCount";
import {ChevronUp} from "@ui-components/icons/ChevronUp";
import {ChevronDown} from "@ui-components/icons/ChevronDown";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const MenuLink = ({
  name,
  href,
  Icon,
  initialUrl,
  externalHref,
  dropdown,
  notificationsCount,
  slim,
  inner,
  last,
  isDropdownOpen,
  toggleDropdown,
  closeDropdown,
  homeLinks,
}) => {
  
  // context
  const {
    country: { code },
  } = useContext(CountryContext);

  // hooks
  const intl = useIntl();
  const { pathname } = useLocation();

  // refs
  const dropdownRef = useRef(null);

  // utilities
  const active = pathname.indexOf(href) >= 0;
  const innerActive = dropdown?.some((d) => pathname.indexOf(d.href) >= 0);
  const retrieveExternalUrl = (url) => {
    const link = homeLinks?.find((link) => link.locale_key === url);
    return link?.external_link_url;
  }
  const handleClickOutside = (e) => {
    if (!slim) return;
    if (!dropdownRef.current?.contains(e.target))
      closeDropdown();
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  const content = (
    <div className={`flex flex-row items-center ${slim && !inner ? "justify-center" : "w-full justify-between"}`}>
      <div className={"flex flex-row items-center gap-2"} title={intl.formatMessage({id: name})}>
        {!!Icon && <Icon aria-hidden="true" />}
        {(!slim || inner) && <FormattedMessage id={name} />}
        <NotificationsCount
          count={notificationsCount}
          roundedCls="rounded-full"
          textCls="text-xxs"
          marginLeftCls={slim && !inner ? "absolute top-0" : ""}
          paddingCls=""
          style={{
            padding: "0.15rem 0.25rem 0.175rem 0.25rem",
            minWidth: "0.9rem",
            textAlign: "center",
            ...(slim && !inner ? {right: "calc(50% - 1.5rem)"} : {}),
          }}
        />
      </div>
      {!slim && !!dropdown && (
        <div className="w-3 mr-1">
          {isDropdownOpen ? <ChevronUp /> : <ChevronDown />}
        </div>
      )}
    </div>
  )

  const classes = classNames(
    "text-black-500 hover:bg-ivory whitespace-nowrap text-sm",
    (active && !inner) || (slim && innerActive) ? "bg-ivory" : "",
    "flex items-center relative block w-full",
    slim ? "" : "w-full rounded",
    slim && !inner ? "justify-center max-w-min rounded-full" : "",
    inner ? "font-normal" : "font-semibold",
    inner && !slim ? "pl-12 py-2 pr-2" : "",
    inner && slim ? "p-3 hover:bg-ivory" : "",
    inner && slim && !last ? "border-b border-gray-300" : "",
  );

  const configHref = retrieveExternalUrl(externalHref);
  if (configHref)
    return (
      <a
        rel="noreferrer"
        target="_blank"
        href={configHref}
        className={classes}
        onClick={closeDropdown}
      >
        {content}
      </a>
    );

  if (!dropdown?.length)
    return (
      <Link
        to={`/${code}${initialUrl || href}`}
        className={classes}
        onClick={closeDropdown}
      >
        {content}
      </Link>
    );
  
  return (
    <div className={`cursor-pointer relative ${!slim ? "w-full" : ""}`}>
      <div className={classes} onClick={toggleDropdown}>
        {content}
      </div>
      <div
        className={`flex flex-col ${slim ? "z-10 bg-white shadow-lg rounded-xl overflow-hidden" : ""} ${isDropdownOpen ? "" : "hidden"}`}
        style={slim ? {position: "absolute", top: 0, left: "calc(100% + 0.5rem)", minWidth: "10rem" } : {}}
        ref={dropdownRef}
      >
        {dropdown.map((d, idx) => (
          <MenuLink
            key={d.name}
            {...d}
            slim={slim}
            last={idx === dropdown.length - 1}
            closeDropdown={slim ? closeDropdown : () => {}}
            homeLinks={homeLinks}
            inner
          />
        ))}
      </div>
    </div>
  );
};

export default MenuLink;
